/* eslint-disable no-console */
/* eslint-disable no-debugger */
import { FC, ReactElement, useState, useCallback } from 'react'

import { fetchBootstrap, updateSettings } from '../../features/appConfig/appConfigSlice'
import { useI18n } from '../../hooks'
import { useAppDispatch, useAppSelector } from '../../store'
import { Currency, Locale, Settings } from '../../types'
import { titlize } from '../../utils'
import { scrollPageToTop } from '../../utils/domHelpers'
import Button from '../atoms/buttons/button'
import { TailLoader } from '../loaders'
import RedirectionTimer from '../redirectionTimer'
import { openSnackbar } from '../snackbar'
import './index.css'

const LocaleSelection: FC<{ settings: Settings; onClose: () => void }> = ({ settings, onClose }): ReactElement => {
  const dispatch = useAppDispatch()
  const { bootstrap } = useAppSelector((state) => state.appConfigReducer)

  const { t } = useI18n()
  const [localeSelection, setLocaleSelection] = useState<Locale>(settings.locale.language as Locale)
  const [currencySelection, setCurrencySelection] = useState<Currency>(settings.currency.currencyAbbreviation as Currency)
  const [settingsSelection, setSettingsSelection] = useState<Settings>(settings)
  const [loading, setLoading] = useState<boolean>(false)

  const handleUpdateSettings = useCallback(async () => {
    try {
      setLoading(true)
      await dispatch(updateSettings(settingsSelection))
      await dispatch(fetchBootstrap())
      setLoading(false)
      onClose()
      scrollPageToTop()
    } catch (error) {
      openSnackbar(
        <div>
          {t('LocaleSelection_changeLocalizationErrorMessage')}{' '}
          <b className="heavy">
            <RedirectionTimer startTime={5} callBackFunction={() => window.location.reload()} />
          </b>
        </div>,
        6,
        'topRight',
        'failure'
      )
    }
  }, [dispatch, settingsSelection, onClose, t])

  return (
    <div className="locales-modal-container">
      <div className="locales-modal-wrapper">
        <header className="locales-modal-header">
          <h2 className="heavy">{t('LocaleSelection_languageSupport').toUpperCase()}</h2>
        </header>
        <hr className="locales-header-separator separator" />
        <section className="locales-modal-selection-section">
          <div className="locales-currency-wrapper locales-wrapper">
            <h2 className="locales-modal-sub-header roman">{t('LocaleSelection_availableCultures')}</h2>
            <div className="locales-selection-container" tabIndex={0} role="radio" aria-checked={!!localeSelection}>
              {bootstrap.localizationSettings.supported.map(({ language, languageID, locale }, i) => (
                <div key={locale + `-${i}`} className="locale">
                  <input
                    id={language}
                    type="radio"
                    checked={language === localeSelection}
                    onChange={() => {
                      setLocaleSelection(language as Locale)
                      setSettingsSelection((pre) => ({ ...pre, locale: { language, languageID, locale } }))
                    }}
                  />
                  <label className="light" htmlFor={language}>
                    {titlize(locale)}
                  </label>
                </div>
              ))}
            </div>
          </div>
          <hr className="locales-separator separator" />
          <div className="locales-currency-wrapper">
            <h2 className="locales-modal-sub-header roman">{t('LocaleSelection_availableCurrencies')}</h2>
            <div className="currency-selection-container" tabIndex={0} role="radio" aria-checked={!!currencySelection}>
              {bootstrap.currencySettings.supported.map(({ currencyID, currencyName, currencyAbbreviation }, i) => (
                <div key={currencyName + `-${i}`} className="currency">
                  <input
                    id={currencyAbbreviation}
                    type="radio"
                    checked={currencyAbbreviation === currencySelection}
                    onChange={() => {
                      setCurrencySelection(currencyAbbreviation as Currency)
                      setSettingsSelection((pre) => ({ ...pre, currency: { currencyID, currencyName, currencyAbbreviation } }))
                    }}
                  />
                  <label htmlFor={currencyAbbreviation}>
                    {titlize(currencyName)}({currencyAbbreviation})
                  </label>
                </div>
              ))}
            </div>
          </div>
        </section>
        <footer className="locales-modal-footer">
          <Button className="locales-modal-cancel-btn" value="Ok" onClick={onClose}>
            {t('Form_cancel')}
          </Button>
          <Button className="locales-modal-submit-btn button-primary" value="Ok" onClick={async () => handleUpdateSettings()}>
            {t('Common_confirm')}
          </Button>
        </footer>
      </div>
      {loading && <TailLoader />}
    </div>
  )
}

export default LocaleSelection
